/**
 * Labels, colours and helpers for the validation run's stage rail.
 *
 * The rail is the one piece of vocabulary every screen in this feature
 * shares, so it lives in one place: the list page badge, the detail page
 * stepper, and the tester's "run status" chip all read these.
 */

import type {
  ParticipantRole,
  Readiness,
  RunOut,
  RunStatus,
  TestStatus,
} from "@/shared/api/validation";

/** Badge colour per participant role on a run (ADR-0054). */
export const ROLE_COLOR: Record<ParticipantRole, string> = {
  editor: "blue",
  reviewer: "grape",
  approver: "teal",
  qa_approver: "orange",
};

export interface Stage {
  key: RunStatus;
  /** Short label for the stepper. */
  label: string;
  /** What the stage means, for the stepper description. */
  hint: string;
  /** Which tab of the detail page shows this stage's work. */
  tab: "risk" | "plan" | "tests" | "execution" | "report" | "approval";
}

/** Ordered rail. `rejected` is off the rail (a side exit from `reported`). */
export const STAGES: Stage[] = [
  { key: "draft", label: "Risk assessment", hint: "Attach the release comparison", tab: "risk" },
  { key: "assessed", label: "Confirm risks", hint: "Human gate 1", tab: "risk" },
  { key: "risks_confirmed", label: "Plan", hint: "AI drafts the validation plan", tab: "plan" },
  { key: "planned", label: "Tests", hint: "AI authors and classifies tests", tab: "tests" },
  { key: "authored", label: "Approve & assign", hint: "Human gate 2", tab: "tests" },
  {
    key: "executing",
    label: "Execute",
    hint: "Automatic via Veeva API · manual by testers",
    tab: "execution",
  },
  { key: "reported", label: "Report", hint: "Coverage, defects, readiness", tab: "report" },
  { key: "approved", label: "Sign-off", hint: "Human gate 3, e-signature", tab: "approval" },
];

export const STATUS_LABEL: Record<RunStatus, string> = {
  draft: "Draft",
  assessed: "Risks to confirm",
  risks_confirmed: "Planning",
  planned: "Plan ready",
  authored: "Tests drafted",
  executing: "Executing",
  reported: "Awaiting sign-off",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  closed: "Closed, no verification",
};

export const STATUS_COLOR: Record<RunStatus, string> = {
  draft: "gray",
  assessed: "yellow",
  risks_confirmed: "blue",
  planned: "blue",
  authored: "yellow",
  executing: "blue",
  reported: "yellow",
  approved: "green",
  rejected: "red",
  cancelled: "dark",
  closed: "teal",
};

/** A cancelled run (ADR-0062) as the stage tabs should draw it: the content
 *  of the stage it was cancelled in, with every action switched off (the
 *  server refuses them anyway, 409 `validation_run_cancelled`). */
export function frozenView(run: RunOut): RunOut {
  if (run.status !== "cancelled" && run.status !== "closed") return run;
  return {
    ...run,
    // A closed run (ADR-0088) stands where it ended: at its assessed risks.
    status: run.status === "closed" ? "assessed" : (run.cancelled_from_stage ?? "draft"),
    may_close_early: false,
    may_edit: false,
    is_owner: false,
    my_role: null,
    viewer_user_id: null,
    qa_acceptances_waiting: 0,
    signoff: run.signoff ? { ...run.signoff, may_sign: false, my_step: null } : run.signoff,
  };
}

export const TEST_STATUS_COLOR: Record<TestStatus, string> = {
  draft: "gray",
  approved: "blue",
  assigned: "blue",
  in_progress: "yellow",
  passed: "green",
  failed: "red",
  blocked: "orange",
  withdrawn: "gray",
  accepted_without_execution: "teal",
};

export const TEST_STATUS_LABEL: Record<TestStatus, string> = {
  draft: "Draft",
  approved: "Ready",
  assigned: "Assigned",
  in_progress: "In progress",
  passed: "Passed",
  failed: "Failed",
  blocked: "Blocked",
  withdrawn: "Withdrawn",
  accepted_without_execution: "Accepted without execution",
};

/** Executed: the test is a record and is never edited or removed. */
export const EXECUTED_TEST: TestStatus[] = ["passed", "failed", "blocked"];

/** Closed for execution: executed, or accepted without execution (ADR-0057).
 *  An accepted test is changed only by revoking the acceptance. */
export const CLOSED_TEST: TestStatus[] = [...EXECUTED_TEST, "accepted_without_execution"];

export const READINESS_LABEL: Record<Readiness, string> = {
  go: "GO",
  go_with_conditions: "GO WITH CONDITIONS",
  no_go: "NO-GO",
};

export const READINESS_COLOR: Record<Readiness, string> = {
  go: "green",
  go_with_conditions: "yellow",
  no_go: "red",
};

/** GxP risk word → colour, matching the risk report's own ramp. */
export function riskColor(rating: string | null | undefined): string {
  switch ((rating ?? "").toLowerCase()) {
    case "high":
      return "red";
    case "medium":
      return "yellow";
    case "low":
      return "gray";
    default:
      return "gray";
  }
}

/** The GxP ratings a person may give a risk item. */
export const RATINGS = ["High", "Medium", "Low", "N/A"] as const;

/** Human labels for the ITQRA buckets — mirrors validation_itqra.LABELS. */
export const ITQRA_LABEL: Record<string, Record<string, string>> = {
  process_type: {
    calculation_validation: "Calculation / Validation",
    change_of_data: "Change of data",
    display: "Display",
  },
  business_impact: {
    compliance: "Compliance",
    wrong_information: "Wrong information",
    negligible: "Negligible",
  },
  use_frequency: { very_often: "Very often", often: "Often", rare: "Rare" },
};

/** Index on the rail, or -1 for rejected. */
export function stageIndex(status: RunStatus): number {
  return STAGES.findIndex((s) => s.key === status);
}

/** Which tab the run is "at" — where the next human action lives. */
export function activeTab(status: RunStatus): Stage["tab"] {
  if (status === "rejected") return "report";
  if (status === "cancelled" || status === "closed") return "risk";
  return STAGES[stageIndex(status)]?.tab ?? "risk";
}

/** Whether the run's validation method allows downloading the document in
 *  `fmt` in its current state (ADR-0079 §5.1). A server without the method
 *  lists nothing: everything is allowed, as before. */
export function allowsFormat(run: RunOut, kind: string, fmt: "md" | "pdf"): boolean {
  const allowed = run.export_formats?.[kind];
  return !allowed || allowed.includes(fmt);
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}
